import { useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import type { ProofIndexEntry } from "@/components/codesmith/proof-index/proofIndexEntry";
import { useApi } from "./useApi";
import { orgInstanceSlug, useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import { useDebouncedValue } from "./useDebouncedValue";
import { useOrgFromPathname } from "./useOrgFromPathname";
const QUERY_KEY = "proofSearch";
const MIN_QUERY_LENGTH = 2;
const MAX_SEARCH_RESULTS = 8;
// Matches the session text search's pace, so both waves land together.
const SEARCH_DEBOUNCE_MS = 150;

/**
 * `acme/web#41` → `{ repository: "acme/web", number: "41" }`. The listing
 * matches a PR URL or a bare `#41`, not this form: its number is sent and
 * the repository narrows the answer here.
 */
export function repoReferenceIn(phrase: string): {
  repository: string;
  number: string;
} | null {
  const match = /^([\w.-]+\/[\w.-]+)#([1-9]\d{0,17})$/.exec(phrase);
  return match ? {
    repository: match[1],
    number: match[2]
  } : null;
}

/**
 * Proof search for the palette: the typed phrase against the org's proofs
 * listing, which matches a PR URL, `#41`, a title or `owner/repo`, so every
 * proof answers, paged into the sidebar or not. Each debounced phrase is its
 * own query, so an older answer never shows for a newer phrase; closing the
 * palette (`enabled` false) cancels the request in flight.
 */
export function useProofSearch(searchTerm: string, enabled = true) {
  const api = useApi();
  const queryClient = useQueryClient();
  const activeGithubOrg = useOrgFromPathname();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const installation = connectedGithubOrgs?.installations.find(candidate => candidate?.account?.login === activeGithubOrg);
  const instanceSlug = orgInstanceSlug(connectedGithubOrgs, activeGithubOrg ?? null);
  const searchable = !!activeGithubOrg && !!instanceSlug && (installation?.codesmith_enabled ?? false) && (installation?.proof_enabled ?? false);
  const trimmed = searchTerm.trim();
  const queryActive = searchable && trimmed.length >= MIN_QUERY_LENGTH;
  const phrase = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  const {
    data,
    isFetching
  } = useQuery<ProofIndexEntry[]>([QUERY_KEY, instanceSlug, activeGithubOrg, phrase], async ({
    signal
  }) => {
    // Loaded on demand: the listing's parsers would otherwise ride in the
    // shared bundle that hosts the palette.
    const {
      fetchOrgProofsPage
    } = await import("@/components/codesmith/proof-index/useOrgProofs");
    const reference = repoReferenceIn(phrase);
    const page = await fetchOrgProofsPage(api, instanceSlug!, activeGithubOrg!, {
      search: reference ? `#${reference.number}` : phrase,
      filterParams: {},
      cursor: undefined
    }, signal);
    const entries = reference ? page.entries.filter(entry => entry.repository.toLowerCase() === reference.repository.toLowerCase()) : page.entries;
    return entries.slice(0, MAX_SEARCH_RESULTS);
  }, {
    enabled: enabled && searchable && phrase.length >= MIN_QUERY_LENGTH,
    // The next keystroke re-queries anyway.
    retry: false,
    refetchOnWindowFocus: false,
    // Proof rows are a bonus beside the session rows; a failure drops them.
    onError: () => {}
  });
  useEffect(() => {
    if (enabled) return;
    void queryClient.cancelQueries([QUERY_KEY]);
  }, [enabled, queryClient]);
  return {
    proofs: enabled && queryActive ? data ?? [] : [],
    isLoading: enabled && queryActive && (phrase !== trimmed || isFetching)
  };
}